import React from 'react';
import { Avatar, Box, makeStyles, Typography } from '@popmenu/common-ui';
import { Skeleton } from '@material-ui/lab';
import { FormattedMessage } from 'react-intl';
import { Redirect } from 'react-router-dom';
import { useRouteParams } from '../../hooks/useRouteParams';
import { useDishListBySlugData } from '../../hooks/data/useDishListBySlugData';
import { DishCardsSkeleton } from '../DishCardsSkeleton';
import { DishFeedCard } from '../DishFeedCard';
import { Meta } from '../Meta';

const useStyles = makeStyles(theme => ({
  cardContainer: {
    [theme.breakpoints.down('md')]: {
      flex: `0 1 calc(33.33% - ${theme.spacing(3)}px)`,
    },
    [theme.breakpoints.down('sm')]: {
      flex: `0 1 calc(50% - ${theme.spacing(3)}px)`,
    },
    [theme.breakpoints.down('xs')]: {
      flex: '0 1 100%',
    },
    flex: `0 1 calc(25% - ${theme.spacing(3)}px)`,
    marginBottom: theme.spacing(6),
  },
  cardSkeleton: {
    borderRadius: theme.spacing(1),
    height: 0,
    paddingTop: '75%',
  },
  dishesContainer: {
    // Only show 1 row of skeleton cards based on screen size
    [theme.breakpoints.down('md')]: {
      '& .infinite-skeleton:nth-of-type(n+4)': {
        display: 'none',
      },
    },
    [theme.breakpoints.down('sm')]: {
      '& .skeleton:nth-of-type(n+3)': {
        display: 'none',
      },
    },
    [theme.breakpoints.down('xs')]: {
      '& .infinite-skeleton:nth-of-type(n+2)': {
        display: 'none',
      },
    },
    '& > div': {
      alignItems: 'stretch',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
  },
  heading: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
      lineHeight: '4rem',
    },
    borderBottom: `1px solid ${theme.palette.gray.light}`,
    marginBottom: theme.spacing(2),
  },
  headingContainer: {
    marginBottom: theme.spacing(6),
  },
  headingSkeleton: {
    height: '4rem',
  },
  noResultsContainer: {
    padding: theme.spacing(8, 4),
    textAlign: 'center',
    width: '100%',
  },
  userAvatar: {
    float: 'left',
    height: theme.spacing(3),
    marginRight: theme.spacing(1),
    width: theme.spacing(3),
  },
  userContainer: {
    overflow: 'auto',
  },
  userName: {
    float: 'left',
  },
}));

export const DishListPage = () => {
  const classes = useStyles();
  const dishListFriendlySlug = useRouteParams().dishListSlug ?? '';
  const dishListSlug = dishListFriendlySlug.split('-')[0];

  const { data } = useDishListBySlugData({ slug: dishListSlug });
  const dishList = data?.dishList;
  const dishes = dishList?.menuItems?.records ?? [];

  if (!dishList) {
    return (
      <Box>
        <Box className={classes.headingContainer}>
          <Skeleton className={classes.headingSkeleton} />
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </Box>
        <Box className={classes.dishesContainer}>
          <div>
            <DishCardsSkeleton
              cardContainerClassName={classes.cardContainer}
              cardSkeletonClassName={classes.cardSkeleton}
              length={12}
            />
          </div>
        </Box>
      </Box>
    );
  }

  const listPath = `/lists/${dishList.friendlySlug}`;

  // After loading dish list, ensure slug in URL matches expected slug
  if (dishListFriendlySlug !== dishList.friendlySlug) {
    return (
      <Redirect to={listPath} />
    );
  }

  return (
    <Box>
      <Meta
        canonicalPath={listPath}
        imageUrl={dishList.photoUrl ?? undefined}
        title={dishList.name}
      />
      <Box className={classes.headingContainer}>
        <Box className={classes.userContainer} component="aside">
          <Avatar
            alt={dishList.userDisplayName}
            className={classes.userAvatar}
            src={dishList.userAvatarUrl}
          />
          <Typography className={classes.userName} component="span">
            {dishList.userDisplayName}
          </Typography>
        </Box>
        <Typography className={classes.heading} component="h1" variant="h2">
          {dishList.name}
        </Typography>
        {dishList.description && (
          <Typography>
            {dishList.description}
          </Typography>
        )}
      </Box>
      <Box className={classes.dishesContainer}>
        <div>
          {dishes.length === 0 && (
            <Box className={classes.noResultsContainer}>
              <Typography>
                <FormattedMessage
                  defaultMessage="Sorry, no dishes have been added to this list yet."
                />
              </Typography>
            </Box>
          )}
          {dishes.map(dish => (
            <Box key={dish.id} className={classes.cardContainer} component="section">
              <DishFeedCard
                {...dish}
                dishPathPrefix={`/lists/${dishList.friendlySlug}`}
              />
            </Box>
          ))}
        </div>
      </Box>
    </Box>
  );
};
